import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function SubscribePopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    if (localStorage.getItem("pc_subscribed")) return;
    if (sessionStorage.getItem("pc_popup_dismissed")) return;
    const dismissedAt = Number(localStorage.getItem("pc_popup_dismissed_at") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;

    let shown = false;
    const show = () => {
      if (!shown) {
        shown = true;
        setOpen(true);
      }
    };
    const t = setTimeout(show, 10000);
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total > 0 && window.scrollY / total >= 0.38) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem("pc_popup_dismissed", "1");
    localStorage.setItem("pc_popup_dismissed_at", String(Date.now()));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email, source: "popup" });
      setDone(true);
      localStorage.setItem("pc_subscribed", "1");
      setTimeout(close, 2600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email address");
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/50 backdrop-blur-sm p-4 sm:p-6"
          onClick={close}
          data-testid="subscribe-popup-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="relative w-full max-w-md max-h-[88vh] overflow-y-auto bg-cream rounded-3xl border border-pool/15 shadow-2xl px-7 py-9 sm:px-10 text-center"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscribe-popup"
          >
            <button
              onClick={close}
              aria-label="Close subscribe popup"
              className="absolute top-4 right-4 p-2 rounded-full bg-white/80 border border-slate-200/70 shadow-sm text-slate-600 hover:text-ink hover:bg-white transition-colors"
              data-testid="subscribe-popup-close"
            >
              <X className="h-5 w-5" />
            </button>
            <img src="/logo.png" alt="Poolside Cooking" className="h-16 sm:h-20 w-auto mx-auto" data-testid="subscribe-popup-logo" />
            <h2 className="mt-5 font-display font-black tracking-tight leading-[1.02] text-2xl sm:text-3xl text-ink" data-testid="subscribe-popup-headline">
              <span className="text-pool-deep">STAY</span> <span className="text-pool">POOLSIDE.</span>
            </h2>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed" data-testid="subscribe-popup-copy">
              Special guests, new episodes, events, recipes and more — straight to your inbox.
            </p>
            {done ? (
              <p
                className="mt-7 inline-flex items-center gap-2.5 bg-white border border-pool/30 text-pool-deep font-semibold text-sm px-6 py-3.5 rounded-full"
                data-testid="subscribe-popup-confirmation"
              >
                <Check className="h-4 w-4 text-pool" /> You&rsquo;re Poolside. We&rsquo;ll keep you in the loop.
              </p>
            ) : (
              <form onSubmit={submit} className="mt-7 flex flex-col gap-3" data-testid="subscribe-popup-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  aria-label="Email address"
                  className="w-full bg-white border border-slate-200 text-ink placeholder:text-slate-400 text-sm px-5 py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-pool/50 focus:border-pool"
                  data-testid="subscribe-popup-email"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark disabled:opacity-60 text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-colors"
                  data-testid="subscribe-popup-submit"
                >
                  {busy ? "Joining…" : <>Stay Poolside <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            )}
            <p className="mt-4 text-slate-400 text-[11px]" data-testid="subscribe-popup-note">
              No spam. Just good food, good vibes, good updates.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
